export const PROFILE_PHOTO_MAX_BYTES = 2 * 1024 * 1024;

/** Minimum edge length so the avatar does not look blurry on retina displays */
export const PROFILE_PHOTO_MIN_SIDE = 64;

/** Maximum edge length to avoid oversized uploads before server-side processing */
export const PROFILE_PHOTO_MAX_SIDE = 4096;

/** Recommended minimum for a clear circular crop in the UI */
export const PROFILE_PHOTO_RECOMMENDED_MIN = 200;

/** Ideal square size for sharp profile display (navbar uses ~34 logical px; source should be larger) */
export const PROFILE_PHOTO_RECOMMENDED_IDEAL = 512;

export interface ProfilePhotoPickMetadata {
  readonly mimeType: string;
  readonly sizeBytes: number;
  readonly width: number;
  readonly height: number;
}

export interface ProfilePhotoValidationResult {
  readonly valid: boolean;
  readonly errors: readonly string[];
}

function normalizeMime(mime: string): string {
  return mime.toLowerCase().split(';')[0].trim();
}

export function isAllowedProfilePhotoMime(mime: string): boolean {
  const normalized = normalizeMime(mime);
  return (
    normalized === 'image/jpeg' ||
    normalized === 'image/jpg' ||
    normalized === 'image/png' ||
    normalized === 'image/webp'
  );
}

export function validateProfilePhoto(meta: ProfilePhotoPickMetadata): ProfilePhotoValidationResult {
  const errors: string[] = [];

  if (!isAllowedProfilePhotoMime(meta.mimeType)) {
    errors.push('Use JPEG, PNG or WebP.');
  }

  if (meta.sizeBytes <= 0) {
    errors.push('Could not determine file size. Try another image.');
  } else if (meta.sizeBytes > PROFILE_PHOTO_MAX_BYTES) {
    errors.push('File must be 2 MB or smaller.');
  }

  if (meta.width < PROFILE_PHOTO_MIN_SIDE || meta.height < PROFILE_PHOTO_MIN_SIDE) {
    errors.push(
      `Image must be at least ${PROFILE_PHOTO_MIN_SIDE}×${PROFILE_PHOTO_MIN_SIDE} pixels.`
    );
  }

  if (meta.width > PROFILE_PHOTO_MAX_SIDE || meta.height > PROFILE_PHOTO_MAX_SIDE) {
    errors.push(`Each side must be at most ${PROFILE_PHOTO_MAX_SIDE} pixels.`);
  }

  return { valid: errors.length === 0, errors };
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
